import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { customerService, type Customer } from "../../services/customerService";
import {
  DEFAULT_DISPLAY_LOCALE,
  DEFAULT_DISPLAY_TIME_ZONE,
  getDisplayLocalization,
  setDisplayLocalization,
  type DisplayLocalization,
} from "./displayDateTime";

type DisplayLocalizationContextValue = DisplayLocalization & {
  customer: Customer | null;
  ready: boolean;
  apply: (next: DisplayLocalization, nextCustomer?: Customer | null) => void;
  reload: () => Promise<void>;
};

const DisplayLocalizationContext = createContext<DisplayLocalizationContextValue>({
  timeZone: DEFAULT_DISPLAY_TIME_ZONE,
  locale: DEFAULT_DISPLAY_LOCALE,
  customer: null,
  ready: false,
  apply: () => undefined,
  reload: async () => undefined,
});

export function DisplayLocalizationProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [ready, setReady] = useState(false);
  const [localization, setLocalization] = useState<DisplayLocalization>({
    timeZone: DEFAULT_DISPLAY_TIME_ZONE,
    locale: DEFAULT_DISPLAY_LOCALE,
  });

  const applyLocalization = useCallback((next: DisplayLocalization, nextCustomer: Customer | null) => {
    setCustomer(nextCustomer);
    setLocalization(next);
    setDisplayLocalization(next);
  }, []);

  const apply = useCallback(
    (next: DisplayLocalization, nextCustomer?: Customer | null) => {
      applyLocalization(next, nextCustomer === undefined ? customer : nextCustomer);
    },
    [applyLocalization, customer],
  );

  const reload = useCallback(async () => {
    try {
      const nextCustomer = await customerService.getMyCustomer();
      applyLocalization(
        {
          timeZone: nextCustomer?.timeZone?.trim() || getDisplayLocalization().timeZone,
          locale: nextCustomer?.locale?.trim() || getDisplayLocalization().locale,
        },
        nextCustomer,
      );
    } catch {
      applyLocalization(
        {
          timeZone: DEFAULT_DISPLAY_TIME_ZONE,
          locale: DEFAULT_DISPLAY_LOCALE,
        },
        null,
      );
    } finally {
      setReady(true);
    }
  }, [applyLocalization]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo(
    () => ({ ...localization, customer, ready, apply, reload }),
    [localization, customer, ready, apply, reload],
  );

  return (
    <DisplayLocalizationContext.Provider value={value}>
      <Fragment key={`${localization.timeZone}|${localization.locale}`}>{children}</Fragment>
    </DisplayLocalizationContext.Provider>
  );
}

export function useDisplayLocalization() {
  return useContext(DisplayLocalizationContext);
}
